import { Fragment, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'

/**
 * Lista lui bootstrap-select 1.12 (`selectpicker` cu liveSearch), cu markupul
 * pe care il scrie el si gesturile din bootstrap.js pentru dropdown, fara
 * jQuery: invelisul `.btn-group.bootstrap-select` cu clasele <select>-ului,
 * butonul `.btn.dropdown-toggle.btn-default` cu `.filter-option` si `.bs-caret`,
 * meniul `.dropdown-menu.open` cu `.bs-searchbox` si `ul.dropdown-menu.inner`,
 * cate un `li > a > span.text` pe optiune, `.selected` pe cea aleasa si
 * `.active` pe cea sub tasta.
 *
 * Titlul <select>-ului (`title`) e ce arata butonul cand nimic nu e ales, cu
 * `.bs-placeholder`; cautarea e "contine", fara litera mare, si cand nu
 * gaseste nimic scrie `No results matched "..."`. Se inchide la clic in afara,
 * la Escape, si dupa alegere. `onChange` pleaca numai cand valoarea chiar se
 * schimba, cum face `changed.bs.select`.
 *
 * ---------------------------------------------------------------------------
 * `multiple`: bifele, si cele doua butoane
 * ---------------------------------------------------------------------------
 *
 * Cu `multiple`, invelisul primeste `show-tick` (asa face si createDropdown:
 * `this.multiple || options.showTick`), deci bifa din dreapta randului se vede;
 * un clic COMUTA optiunea si meniul RAMANE deschis, ca in clickListener-ul lui.
 *
 * `actionsBox` deseneaza `.bs-actionsbox` intre cautare si lista, cu
 * "Select All" / "Deselect All" - stilul lor e deja in `public/css/app.css`
 * (`.bs-searchbox + .bs-actionsbox`, `.bs-actionsbox .btn-group button`).
 *
 * CELE DOUA BUTOANE LUCREAZA PE CE A RAMAS DUPA CAUTARE, nu pe toata lista.
 * `changeAll()` din bootstrap-select porneste de la
 * `this.$lis.not('.divider, .dropdown-header, .disabled, .hidden')`, iar
 * cautarea e chiar cea care pune `.hidden` pe randurile care nu se potrivesc.
 * Deci "Select All" cu "HQ" scris in casuta alege statiile HQ si le lasa pe
 * celelalte cum erau - si asta e diferenta care se pierde usor la port: un
 * "Select All" care ar bifa TOT ar programa instalarea pe toata firma cand
 * omul filtrase dinadins.
 *
 * `selectedTextFormat` e regula lui din render(): `values` lipeste etichetele
 * cu ", ", iar `count > N` scrie "{0} items selected" de indata ce sunt alese
 * mai mult de N.
 *
 * ---------------------------------------------------------------------------
 * `name`: <select>-ul adevarat ramane in pagina
 * ---------------------------------------------------------------------------
 *
 * bootstrap-select nu inlocuieste <select>-ul, il ASCUNDE
 * (`select.bs-select-hidden { display: none !important }`, tot din app.css) si
 * ii tine optiunile la zi. Cand se da `name`, facem la fel: formularul se
 * serializeaza exact ca inainte de picker - aceleasi campuri, aceleasi valori -
 * si nimic din ce citeste POST-ul nu trebuie schimbat.
 */

export interface SelectPickerOption {
  value: string
  label: string
  /**
   * `data-content` al lui bootstrap-select, cat se foloseste de el la noi: o
   * iconita Font Awesome desenata INAINTEA etichetei, si in lista si pe buton.
   *
   * Lista de iconite a catalogului de servicii asa e scrisa in portal
   * (`form.php`: `data-content="<i class='fas fa-database'></i> Database"`).
   * Fara ea alegerea e un sir de treizeci de cuvinte fara nicio poza - adica
   * exact ce nu e un selector de iconite.
   */
  icon?: string
  /**
   * `data-subtext` al lui bootstrap-select: un rand mic, gri, dupa eticheta
   * (`<small class="text-muted">`). Felul echipamentului, clasa elementului de
   * configurare - ce scria portalul in `subtext` la fiecare optiune de afectat.
   */
  subtext?: string
  /**
   * `<optgroup>` al lui bootstrap-select: randurile cu acelasi `group`, puse
   * unul dupa altul, primesc deasupra un `li.dropdown-header` cu numele lui.
   */
  group?: string
}

interface SelectPickerCommon {
  id: string
  /** Clasele <select>-ului vechi (fara `selectpicker`), copiate pe invelis cum face setStyle. */
  className?: string
  title: string
  options: SelectPickerOption[]
  disabled?: boolean
  /** Numele campului. Dat, <select>-ul adevarat ramane in pagina, ascuns. */
  name?: string
  /** `data-size`: cate randuri incap in meniu inainte sa curga. */
  size?: number
  /**
   * Cautarea se face pe SERVER (ajaxSelectPicker din portal): ce scrie omul
   * pleaca aici, iar `options` sunt chiar raspunsul - lista nu se mai
   * filtreaza si in pagina. Cine il da isi tine singur ritmul (debounce).
   */
  onSearch?(needle: string): void
  /** Cat se asteapta raspunsul cautarii de pe server. */
  searching?: boolean
}

interface SelectPickerSingle extends SelectPickerCommon {
  multiple?: false
  value: string
  onChange(value: string): void
}

interface SelectPickerMulti extends SelectPickerCommon {
  multiple: true
  /** `data-actions-box`: "Select All" / "Deselect All" peste randurile ramase dupa cautare. */
  actionsBox?: boolean
  /** `data-selected-text-format`: `values` sau `count > N`. */
  selectedTextFormat?: string
  value: string[]
  onChange(value: string[]): void
}

export type SelectPickerProps = SelectPickerSingle | SelectPickerMulti

export function SelectPicker(props: SelectPickerProps) {
  const { id, className = '', title, options, disabled = false, name, size, onSearch, searching = false } = props
  const remote = onSearch !== undefined
  const multiple = props.multiple === true
  const actionsBox = multiple && props.actionsBox === true
  const chosenList = multiple ? props.value : props.value === '' ? [] : [props.value]

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [active, setActive] = useState<string | null>(null)
  const [maxHeight, setMaxHeight] = useState<number | null>(null)
  const root = useRef<HTMLDivElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const list = useRef<HTMLUListElement>(null)
  const searchId = useId()

  /* Valoarea goala e "nimic ales": bootstrap-select pune atunci optiunea-titlu
     (bs-title-option) in fata si o alege pe ea, deci butonul scrie titlul. */
  const chosen = new Set(chosenList)
  const picked = options.filter((o) => chosen.has(o.value))
  const placeholder = picked.length === 0
  const text = placeholder ? title : buttonText(picked, multiple ? props.selectedTextFormat : undefined)
  const needle = search.trim().toLowerCase()
  const visible = remote || needle === '' ? options : options.filter((o) => o.label.toLowerCase().includes(needle))

  useEffect(() => {
    if (!open) return
    const away = (ev: MouseEvent) => {
      if (root.current && !root.current.contains(ev.target as Node)) setOpen(false)
    }
    document.addEventListener('click', away)
    const frame = setTimeout(() => input.current?.focus(), 10)
    return () => {
      document.removeEventListener('click', away)
      clearTimeout(frame)
    }
  }, [open])

  /* setSize ('auto'): meniul nu trece de marginea de jos a ferestrei. Cu
     `size`, se opreste si mai devreme - la atatea randuri, masurate pe primul,
     cum face si el in setSize(). */
  useLayoutEffect(() => {
    if (!open || !button.current) return
    const rect = button.current.getBoundingClientRect()
    let room = Math.max(120, window.innerHeight - rect.bottom - 20)
    if (size !== undefined && size > 0) {
      const row = list.current?.querySelector('li')?.getBoundingClientRect().height ?? 0
      if (row > 0) room = Math.min(room, size * row + 60)
    }
    setMaxHeight(room)
  }, [open, size, visible.length])

  const toggle = () => {
    if (disabled) return
    if (!open) {
      setSearch('')
      setActive(chosenList[0] ?? null)
    }
    setOpen(!open)
  }

  /* changed.bs.select: nimic nu pleaca daca valoarea e aceeasi. La `multiple`,
     clicul COMUTA si meniul ramane deschis. */
  const choose = (next: string) => {
    if (multiple) {
      props.onChange(chosen.has(next) ? chosenList.filter((v) => v !== next) : [...chosenList, next])
      setActive(next)
      return
    }
    setOpen(false)
    button.current?.focus()
    if (next !== props.value) props.onChange(next)
  }

  /* changeAll(): numai randurile RAMASE dupa cautare, si numai daca chiar se
     schimba ceva. Vezi capul fisierului. */
  const changeAll = (status: boolean) => {
    if (!multiple) return
    const shown = visible.map((o) => o.value)
    if (status) {
      if (shown.every((v) => chosen.has(v))) return
      props.onChange([...chosenList, ...shown.filter((v) => !chosen.has(v))])
      return
    }
    if (!shown.some((v) => chosen.has(v))) return
    const drop = new Set(shown)
    props.onChange(chosenList.filter((v) => !drop.has(v)))
  }

  const onSearchKey = (ev: KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'Escape') {
      setOpen(false)
      button.current?.focus()
      return
    }
    if (ev.key === 'Enter') {
      ev.preventDefault()
      const pick = visible.find((o) => o.value === active) ?? visible[0]
      if (pick) choose(pick.value)
      return
    }
    if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
      ev.preventDefault()
      if (visible.length === 0) return
      const at = visible.findIndex((o) => o.value === active)
      const step = ev.key === 'ArrowDown' ? 1 : -1
      const nextAt = at < 0 ? (step > 0 ? 0 : visible.length - 1) : (at + step + visible.length) % visible.length
      setActive(visible[nextAt]!.value)
    }
  }

  return (
    <div
      ref={root}
      className={`btn-group bootstrap-select${multiple ? ' show-tick' : ''} ${className}${open ? ' open' : ''}`.trim()}
    >
      {name !== undefined && (
        <select
          id={id}
          name={name}
          className="bs-select-hidden"
          multiple={multiple ? true : undefined}
          value={multiple ? chosenList : props.value}
          disabled={disabled}
          tabIndex={-1}
          aria-hidden="true"
          /* Sursa e pickerul; <select>-ul e copia pe care o citeste formularul,
             exact cum si-l tine bootstrap-select. */
          onChange={() => {}}
        >
          {!multiple && <option value=""></option>}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
      <button
        ref={button}
        type="button"
        className={`btn dropdown-toggle btn-default${placeholder ? ' bs-placeholder' : ''}`}
        data-toggle="dropdown"
        data-id={id}
        role="button"
        title={text}
        aria-expanded={open}
        disabled={disabled}
        onClick={toggle}
      >
        <span className="filter-option pull-left">
          {!placeholder && !multiple && picked[0]?.icon !== undefined && picked[0].icon !== '' && (
            <>
              <i className={`fas ${picked[0].icon}`}></i>{' '}
            </>
          )}
          {text}
        </span>
        &nbsp;
        <span className="bs-caret">
          <span className="caret"></span>
        </span>
      </button>
      <div className="dropdown-menu open" role="combobox" style={maxHeight !== null ? { maxHeight, overflow: 'hidden', minHeight: 0 } : undefined}>
        <div className="bs-searchbox">
          <input
            ref={input}
            id={searchId}
            type="text"
            className="form-control"
            autoComplete="off"
            role="textbox"
            aria-label="Search"
            value={search}
            onChange={(ev) => {
              setSearch(ev.target.value)
              const n = ev.target.value.trim().toLowerCase()
              if (remote) {
                onSearch(ev.target.value)
                return
              }
              const first = options.find((o) => n === '' || o.label.toLowerCase().includes(n))
              setActive(n === '' ? (chosenList[0] ?? null) : first ? first.value : null)
            }}
            onKeyDown={onSearchKey}
          />
        </div>
        {actionsBox && (
          <div className="bs-actionsbox">
            <div className="btn-group btn-group-sm btn-block">
              <button type="button" className="actions-btn bs-select-all btn btn-default" onClick={() => changeAll(true)}>
                Select All
              </button>
              <button type="button" className="actions-btn bs-deselect-all btn btn-default" onClick={() => changeAll(false)}>
                Deselect All
              </button>
            </div>
          </div>
        )}
        <ul
          ref={list}
          className="dropdown-menu inner"
          role="listbox"
          aria-multiselectable={multiple ? true : undefined}
          aria-expanded={open}
          style={maxHeight !== null ? { maxHeight: Math.max(60, maxHeight - 60), overflowY: 'auto', minHeight: 0 } : undefined}
        >
          {visible.map((o, i) => (
            <Fragment key={o.value}>
            {o.group !== undefined && o.group !== '' && (i === 0 || visible[i - 1]!.group !== o.group) && (
              <li className="dropdown-header" role="presentation">
                <span className="text">{o.group}</span>
              </li>
            )}
            <li
              data-original-index={options.indexOf(o)}
              className={`${chosen.has(o.value) ? 'selected' : ''}${o.value === active ? ' active' : ''}`.trim() || undefined}
            >
              <a
                tabIndex={0}
                role="option"
                aria-disabled={false}
                aria-selected={chosen.has(o.value)}
                data-index={i}
                onClick={(ev) => {
                  ev.preventDefault()
                  choose(o.value)
                }}
                onKeyDown={(ev) => {
                  if (ev.key !== 'Enter' && ev.key !== ' ') return
                  ev.preventDefault()
                  choose(o.value)
                }}
                onMouseEnter={() => setActive(o.value)}
              >
                <span className="text">
                  {o.icon !== undefined && o.icon !== '' && <i className={`fas ${o.icon}`}></i>}
                  {o.icon !== undefined && o.icon !== '' ? ' ' : ''}
                  {o.label}
                  {o.subtext !== undefined && o.subtext !== '' && (
                    <>
                      {' '}
                      <small className="text-muted">{o.subtext}</small>
                    </>
                  )}
                </span>
                <span className="fas fa-check check-mark"></span>
              </a>
            </li>
            </Fragment>
          ))}
          {searching && <li className="no-results">Searching...</li>}
          {!searching && needle !== '' && visible.length === 0 && <li className="no-results">No results matched &quot;{search}&quot;</li>}
        </ul>
      </div>
    </div>
  )
}

/**
 * render(): etichetele lipite cu ", ", sau numarul lor cand
 * `selectedTextFormat` e `count` / `count > N` si sunt destule.
 */
function buttonText(picked: SelectPickerOption[], format: string | undefined): string {
  const labels = picked.map((o) => o.label)
  if (format !== undefined && format.includes('count')) {
    const parts = format.split('>')
    const over = Number(parts[1])
    const enough = parts.length > 1 ? labels.length > (Number.isNaN(over) ? 0 : over) : labels.length >= 2
    if (enough) return `${labels.length} item${labels.length === 1 ? '' : 's'} selected`
  }
  return labels.join(', ')
}
